import { useCallback } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";

import { QueryParams, PaginationResponse } from "@regal-voice/ui";
import { renderErrorMessage } from "Services/LoggingService";
import { CommunicationTemplate } from "Types/CommunicationTemplate";

import { removeCommunicationTemplateGql } from "./mutations";
import { findCommunicationTemplatesByBrandQuery } from "./queries";

export function useCommunicationTemplateListData(queryParams: Partial<QueryParams>) {
    const {
        data: { findCommunicationTemplatesByBrand } = {},
        loading,
        refetch,
    } = useQuery<{ findCommunicationTemplatesByBrand: PaginationResponse<CommunicationTemplate> }>(
        findCommunicationTemplatesByBrandQuery,
        {
            fetchPolicy: "cache-and-network",
            notifyOnNetworkStatusChange: true,
            variables: {
                queryParams,
            },
        }
    );

    const [removeCommunicationTemplateMutation, { loading: isDeleting }] = useMutation(removeCommunicationTemplateGql);

    const errorHandler = useCallback((error: unknown, message: string) => {
        renderErrorMessage({
            content: message,
            error: error instanceof Error ? error : new Error(String(error)),
        });
    }, []);

    const removeCommunicationTemplate = useCallback(
        async (id: string) => {
            try {
                const result = await removeCommunicationTemplateMutation({
                    variables: {
                        id,
                    },
                });
                message.success("Successfully deleted Template");
                refetch();
                return result.data;
            } catch (error) {
                errorHandler(error, "Templated failed to be deleted.");
            }
        },
        [removeCommunicationTemplateMutation, errorHandler, refetch]
    );

    return {
        isLoading: loading,
        isDeleting,
        removeCommunicationTemplate,
        findCommunicationTemplatesByBrand,
    };
}
